import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import Sidebar from '../../components/layout/Sidebar';
import Header from '../../components/layout/Header';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import {
  FileText,
  Search,
  Mail,
  Phone,
  Building2,
  Calendar,
  Euro,
  Clock,
  CheckCircle,
  XCircle,
  MessageSquare,
  ExternalLink,
  Loader2,
  ChevronRight,
  Eye,
  Send,
  ArrowUpRight,
  MoreHorizontal
} from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../../components/ui/dropdown-menu';
import axios from 'axios';
import { toast } from 'sonner';
import { cn } from '../../lib/utils';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const STATUS_CONFIG = {
  new: { label: 'Nouveau', color: 'bg-amber-100 text-amber-700', icon: Clock },
  contacted: { label: 'Contacté', color: 'bg-blue-100 text-blue-700', icon: MessageSquare },
  proposal_sent: { label: 'Devis envoyé', color: 'bg-purple-100 text-purple-700', icon: Send },
  accepted: { label: 'Accepté', color: 'bg-green-100 text-green-700', icon: CheckCircle },
  rejected: { label: 'Refusé', color: 'bg-red-100 text-red-700', icon: XCircle },
};

const QuotesManagementPage = () => {
  const { token } = useAuth();
  const { theme } = useTheme();
  const navigate = useNavigate();
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  
  const [quotes, setQuotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  
  const [selectedQuote, setSelectedQuote] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [newStatus, setNewStatus] = useState('');
  const [notes, setNotes] = useState('');

  useEffect(() => {
    fetchQuotes();
  }, [filterStatus]);

  const fetchQuotes = async () => {
    try {
      const params = filterStatus !== 'all' ? `?status=${filterStatus}` : '';
      const response = await axios.get(`${API}/website/quote-requests${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setQuotes(response.data);
    } catch (error) {
      console.error('Error fetching quotes:', error);
      toast.error('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateStatus = async () => {
    if (!selectedQuote || !newStatus) return;
    
    setActionLoading(true);
    try {
      await axios.put(`${API}/website/quote-requests/${selectedQuote.id}`, {
        status: newStatus,
        notes: notes
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Statut mis à jour !');
      setShowDetailModal(false);
      setSelectedQuote(null);
      setNewStatus('');
      setNotes('');
      fetchQuotes();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la mise à jour');
    } finally {
      setActionLoading(false);
    }
  };

  const filteredQuotes = quotes.filter(q => {
    if (!searchQuery) return true;
    const search = searchQuery.toLowerCase();
    return (
      `${q.first_name} ${q.last_name}`.toLowerCase().includes(search) ||
      q.email?.toLowerCase().includes(search) ||
      q.company?.toLowerCase().includes(search) ||
      q.service_type?.toLowerCase().includes(search)
    );
  });

  const stats = {
    new: quotes.filter(q => q.status === 'new').length,
    contacted: quotes.filter(q => q.status === 'contacted').length,
    proposal_sent: quotes.filter(q => q.status === 'proposal_sent').length,
    total: quotes.length
  };

  return (
    <div className="min-h-screen bg-[#f8fafc]">
      <Sidebar collapsed={collapsed} setCollapsed={setCollapsed} mobileOpen={mobileOpen} setMobileOpen={setMobileOpen} />
      
      <div className={cn(
        "transition-all duration-300",
        collapsed ? "md:ml-20" : "md:ml-64"
      )}>
        <Header collapsed={collapsed} setMobileOpen={setMobileOpen} />
        
        <main className="px-4 md:px-8 pb-6 md:pb-8 pt-24">
          {/* Header */}
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
            <div>
              <h1 className="text-2xl md:text-3xl font-bold" style={{ color: theme.colors?.primary }}>
                Demandes de devis
              </h1>
              <p className="text-slate-500 mt-1">
                Gérez les demandes de devis reçues depuis le site
              </p>
            </div>
          </div>

          {/* Stats Cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            <Card className="card">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center">
                    <Clock className="w-5 h-5 text-amber-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-slate-900">{stats.new}</p>
                    <p className="text-xs text-slate-500">Nouveaux</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card className="card">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center">
                    <MessageSquare className="w-5 h-5 text-blue-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-slate-900">{stats.contacted}</p>
                    <p className="text-xs text-slate-500">Contactés</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card className="card">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-purple-100 flex items-center justify-center">
                    <Send className="w-5 h-5 text-purple-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-slate-900">{stats.proposal_sent}</p>
                    <p className="text-xs text-slate-500">Devis envoyés</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card className="card">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-slate-100 flex items-center justify-center">
                    <FileText className="w-5 h-5 text-slate-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-slate-900">{stats.total}</p>
                    <p className="text-xs text-slate-500">Total</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Filters */}
          <div className="flex flex-col sm:flex-row gap-4 mb-6">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                placeholder="Rechercher..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
              />
            </div>
            <Select value={filterStatus} onValueChange={setFilterStatus}>
              <SelectTrigger className="w-full sm:w-48">
                <SelectValue placeholder="Filtrer par statut" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tous les statuts</SelectItem>
                <SelectItem value="new">Nouveaux</SelectItem>
                <SelectItem value="contacted">Contactés</SelectItem>
                <SelectItem value="proposal_sent">Devis envoyés</SelectItem>
                <SelectItem value="accepted">Acceptés</SelectItem>
                <SelectItem value="rejected">Refusés</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Quotes List */}
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
            </div>
          ) : filteredQuotes.length === 0 ? (
            <Card className="card">
              <CardContent className="py-12 text-center">
                <FileText className="w-12 h-12 mx-auto text-slate-300 mb-4" />
                <p className="text-slate-500">Aucune demande de devis trouvée</p>
              </CardContent>
            </Card>
          ) : (
            <Card className="card overflow-hidden">
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-slate-50/50 hover:bg-slate-50/50">
                      <TableHead className="text-xs font-medium">Contact</TableHead>
                      <TableHead className="text-xs font-medium">Entreprise</TableHead>
                      <TableHead className="text-xs font-medium">Service</TableHead>
                      <TableHead className="text-xs font-medium">Budget</TableHead>
                      <TableHead className="text-xs font-medium">Statut</TableHead>
                      <TableHead className="text-xs font-medium">Date</TableHead>
                      <TableHead className="text-xs font-medium text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredQuotes.map((quote) => {
                      const statusConfig = STATUS_CONFIG[quote.status] || STATUS_CONFIG.new;
                      const StatusIcon = statusConfig.icon;
                      
                      return (
                        <TableRow 
                          key={quote.id} 
                          className="cursor-pointer hover:bg-slate-50 transition-colors"
                          onClick={() => {
                            setSelectedQuote(quote);
                            setNewStatus(quote.status);
                            setNotes(quote.notes || '');
                            setShowDetailModal(true);
                          }}
                        >
                          <TableCell className="py-2">
                            <div className="min-w-0">
                              <p className="font-medium text-sm text-slate-800">
                                {quote.first_name} {quote.last_name}
                              </p>
                              <p className="text-xs text-slate-400 truncate max-w-[180px]">
                                {quote.email}
                              </p>
                            </div>
                          </TableCell>
                          <TableCell className="py-2">
                            <span className="text-sm text-slate-600">{quote.company || 'Particulier'}</span>
                          </TableCell>
                          <TableCell className="py-2">
                            <span className="text-sm text-slate-600">{quote.service_type || 'Projet'}</span>
                          </TableCell>
                          <TableCell className="py-2">
                            <span className="text-sm text-slate-600">{quote.budget || 'À définir'}</span>
                          </TableCell>
                          <TableCell className="py-2">
                            <Badge className={cn("text-[10px] h-5", statusConfig.color)}>
                              <StatusIcon className="w-3 h-3 mr-1" />
                              {statusConfig.label}
                            </Badge>
                          </TableCell>
                          <TableCell className="py-2">
                            <span className="text-xs text-slate-500">
                              {new Date(quote.created_at).toLocaleDateString('fr-FR')}
                            </span>
                          </TableCell>
                          <TableCell className="py-2 text-right">
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                                <Button variant="ghost" size="icon" className="h-8 w-8">
                                  <MoreHorizontal className="w-4 h-4" />
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end">
                                <DropdownMenuItem onClick={(e) => {
                                  e.stopPropagation();
                                  setSelectedQuote(quote);
                                  setNewStatus(quote.status);
                                  setNotes(quote.notes || '');
                                  setShowDetailModal(true);
                                }}>
                                  <Eye className="w-4 h-4 mr-2" />
                                  Voir détails
                                </DropdownMenuItem>
                                {quote.email && (
                                  <DropdownMenuItem onClick={(e) => {
                                    e.stopPropagation();
                                    window.open(`mailto:${quote.email}`, '_blank');
                                  }}>
                                    <Mail className="w-4 h-4 mr-2" />
                                    Envoyer email
                                  </DropdownMenuItem>
                                )}
                                {quote.phone && (
                                  <DropdownMenuItem onClick={(e) => {
                                    e.stopPropagation();
                                    window.open(`tel:${quote.phone}`, '_blank');
                                  }}>
                                    <Phone className="w-4 h-4 mr-2" />
                                    Appeler
                                  </DropdownMenuItem>
                                )}
                              </DropdownMenuContent>
                            </DropdownMenu>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>
            </Card>
          )}
        </main>
      </div>

      {/* Detail Modal */}
      <Dialog open={showDetailModal} onOpenChange={setShowDetailModal}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle style={{ color: theme.colors?.primary }}>
              Demande de devis - {selectedQuote?.first_name} {selectedQuote?.last_name}
            </DialogTitle>
          </DialogHeader>
          
          {selectedQuote && (
            <div className="space-y-6">
              {/* Contact Info */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1">
                  <label className="text-xs text-slate-500">Nom complet</label>
                  <p className="font-medium">{selectedQuote.first_name} {selectedQuote.last_name}</p>
                </div>
                <div className="space-y-1">
                  <label className="text-xs text-slate-500">Entreprise</label>
                  <p className="font-medium">{selectedQuote.company || 'Particulier'}</p>
                </div>
                <div className="space-y-1">
                  <label className="text-xs text-slate-500">Email</label>
                  <a href={`mailto:${selectedQuote.email}`} className="font-medium text-cyan-600 hover:underline flex items-center gap-1">
                    {selectedQuote.email}
                    <ExternalLink className="w-3 h-3" />
                  </a>
                </div>
                <div className="space-y-1">
                  <label className="text-xs text-slate-500">Téléphone</label>
                  {selectedQuote.phone ? (
                    <a href={`tel:${selectedQuote.phone}`} className="font-medium text-cyan-600 hover:underline">
                      {selectedQuote.phone}
                    </a>
                  ) : <p className="text-slate-400">-</p>}
                </div>
              </div>

              {/* Project Details */}
              <div className="bg-slate-50 rounded-xl p-4 space-y-3">
                <h4 className="font-semibold text-slate-800">Détails du projet</h4>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-1">
                    <label className="text-xs text-slate-500">Type de service</label>
                    <p className="font-medium">{selectedQuote.service_type || '-'}</p>
                  </div>
                  <div className="space-y-1">
                    <label className="text-xs text-slate-500">Budget estimé</label>
                    <p className="font-medium">{selectedQuote.budget || 'À définir'}</p>
                  </div>
                  <div className="space-y-1">
                    <label className="text-xs text-slate-500">Délai souhaité</label>
                    <p className="font-medium">{selectedQuote.timeline || '-'}</p>
                  </div>
                  <div className="space-y-1">
                    <label className="text-xs text-slate-500">Date de demande</label>
                    <p className="font-medium">{new Date(selectedQuote.created_at).toLocaleDateString('fr-FR')}</p>
                  </div>
                </div>
              </div>

              {/* Description */}
              {selectedQuote.description && (
                <div className="space-y-2">
                  <label className="text-xs text-slate-500">Description du projet</label>
                  <p className="text-slate-700 whitespace-pre-wrap">{selectedQuote.description}</p>
                </div>
              )}

              {/* Status Update */}
              <div className="border-t pt-4 space-y-4">
                <h4 className="font-semibold text-slate-800">Mettre à jour le statut</h4>
                <div className="space-y-2">
                  <Label>Nouveau statut</Label>
                  <Select value={newStatus} onValueChange={setNewStatus}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="new">Nouveau</SelectItem>
                      <SelectItem value="contacted">Contacté</SelectItem>
                      <SelectItem value="proposal_sent">Devis envoyé</SelectItem>
                      <SelectItem value="accepted">Accepté</SelectItem>
                      <SelectItem value="rejected">Refusé</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Notes internes</Label>
                  <Textarea
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    placeholder="Notes sur cette demande..."
                    rows={3}
                  />
                </div>
              </div>
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDetailModal(false)}>
              Fermer
            </Button>
            <Button 
              onClick={handleUpdateStatus}
              disabled={actionLoading}
              style={{ backgroundColor: theme.colors?.accent }}
            >
              {actionLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Mettre à jour
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default QuotesManagementPage;
